import actionCreator from "helpers/actionCreator"
import { createReducer } from "reduxsauce"
import { takeLatest, call, select, put, getContext } from "redux-saga/effects"
import { get, toUpper, isEmpty } from "lodash"
import { sendQuery } from "api/graphql"
import { computeSecurityInfo } from "helpers/topics"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Creators as modelCreators } from "model/data"
import { Creators as dialogCreators, Types as DialogTypes } from "modules/dialog/model"
import { Creators as researchTopicCreators } from "modules/research-topic-root/model"
import { Creators as searchCreators } from "modules/unified-search/model"
import { getApiErrorMessage } from "modules/resources/helpers"

import { getModel } from "./selectors"

export const { Types, Creators } = actionCreator("ompnt/modules/researchTopicCreate", {
  setStep: ["step"],
  setModel: ["modelId"],
  setSelectedDate: ["selectedDate"],
  resetSelectedDate: [],
  setInstrument: ["instrument"],
  createTopic: {
    props: ["data"],
    track: true,
    trackType: "resources-create-complete",
    trackPayload: () => ({ resourceType: "topic" }),
  },
  setCreating: ["value"],
  reset: [],
})

const EMPTY_OBJECT = {}
const DEFAULT_STATE = {
  step: 0,
  selectedDate: null,
  selectedModel: null,
  instrument: EMPTY_OBJECT,
  creating: false,
}

function handleSetModel(state, { modelId }) {
  return { ...state, ...DEFAULT_STATE, selectedModel: modelId }
}

function handleSetSelectedDate(state, { selectedDate }) {
  return { ...state, selectedDate }
}

function handleResetSelectedDate(state) {
  return { ...state, selectedDate: null }
}

function handleSetInstrument(state, { instrument }) {
  return {
    ...state,
    instrument,
    selectedDate: null,
    step: isEmpty(instrument) ? 0 : 1,
  }
}

function handleSetStep(state, { step }) {
  return { ...state, step }
}

function handleCreateTopic(state) {
  return { ...state, creating: true }
}
function handleSetCreating(state, { value }) {
  return { ...state, creating: value }
}
function handleReset(state) {
  return { ...state, ...DEFAULT_STATE }
}

export default createReducer(DEFAULT_STATE, {
  [Types.SET_MODEL]: handleSetModel,
  [Types.SET_SELECTED_DATE]: handleSetSelectedDate,
  [Types.RESET_SELECTED_DATE]: handleResetSelectedDate,
  [Types.SET_INSTRUMENT]: handleSetInstrument,
  [Types.SET_STEP]: handleSetStep,
  [Types.CREATE_TOPIC]: handleCreateTopic,
  [Types.SET_CREATING]: handleSetCreating,
  [Types.RESET]: handleReset,
})

function* startCreateTopic({ data }) {
  if (data) {
    const { account, modelId } = yield select(state => ({
      account: state.global.selected.account,
      modelId: getModel(state),
    }))

    const { name, description, selectedDate, instrument } = data
    const { type, id } = instrument || EMPTY_OBJECT
    const portfolioId = type === "portfolio" ? id : undefined
    const securityId = type === "security" ? computeSecurityInfo(instrument) : undefined

    const topic = {
      name,
      description,
      selectedDate,
      referenceInstrument: {
        type: toUpper(type),
        ...(portfolioId && { portfolioId }),
        ...(securityId && { securityId }),
      },
    }

    const mutation = `mutation ($topic: NewResearchTopic!) {
      createResearchTopic (researchTopic: $topic) {
        id,
        name,
        description,
        selectedDate
        referenceInstrument {
          type,
          ${securityId ? "securityId { ticker, mic, country }" : ""}
          ${portfolioId ? "portfolioId" : ""}
        },
        lastUpdated
      }
    }`

    try {
      const { data, apiMessages } = yield call(sendQuery, mutation, { topic }, account)
      const result = get(data, "createResearchTopic")

      if (result) {
        yield put(dialogCreators.close())
        yield put(Creators.reset())
        yield put(notificationCreators.add("info", null, `New research topic "${topic.name}" successfully created.`))
        yield put(modelCreators.graphqlCreateSuccess("accountResearchTopics", "account-research-topics", "researchTopics", result))
        yield put(modelCreators.resetCollections(["resourcesPermissions"]))
        yield put(researchTopicCreators.setModel(modelId))
        const { navigate } = yield getContext("router")
        yield call(navigate, `/${account}/t/${result.id}/experiments`)
      } else {
        yield put(Creators.setCreating(false))
        yield put(notificationCreators.add("error", null, getApiErrorMessage(apiMessages, "research topic")))
      }
    } catch {
      yield put(notificationCreators.add("error", null, "An unknown error occurred while creating the research topic."))
    }
  }
}

function* handleClose() {
  yield put(Creators.reset())
  yield put(searchCreators.close())
}

export function* saga() {
  yield takeLatest(Types.CREATE_TOPIC, startCreateTopic)
  yield takeLatest(DialogTypes.CLOSE, handleClose)
}
